import React, { useState, useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
} from '@mui/material';

import { formatDistance } from '../../../utils/fixedRoutes/distanceFormatter';
import type { DistanceUnit } from '../../../utils/fixedRoutes/distanceFormatter';
import type { OptimizationResult } from '../../../utils/fixedRoutes/optimizeRoute';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
interface RouteOptimizationModalProps {
  open: boolean;
  onClose: () => void;
  optimizationResult: OptimizationResult | null;
  distanceUnit: DistanceUnit;
}

export default function RouteOptimizationModal({
  open,
  onClose,
  optimizationResult,
  distanceUnit,
}: RouteOptimizationModalProps) {
  const { t } = useTranslation(['mapDialogs', 'common']);
  const originalChipRefs = useRef<Array<HTMLDivElement | null>>([]);
  const optimizedChipRefs = useRef<Array<HTMLDivElement | null>>([]);

  if (!optimizationResult) return null;

  const absDistanceSaved = Math.abs(optimizationResult.distanceSaved);
  const absSavingsPercent = Math.abs(parseFloat(optimizationResult.savingsPercent));
  const isNoChange = absDistanceSaved < 0.1 && absSavingsPercent < 0.1;
  const isSameOrder = optimizationResult.originalOrder.length === optimizationResult.optimizedOrder.length &&
    optimizationResult.originalOrder.every((stop, idx) => stop === optimizationResult.optimizedOrder[idx]);
  const isAlreadyOptimal = isNoChange && isSameOrder;
  const isImproved = optimizationResult.distanceSaved > 0 && !isAlreadyOptimal;

  // Create mapping: originalIndex -> optimizedIndex
  const originalToOptimized: Map<number, number> = new Map();
  optimizationResult.originalOrder.forEach((stopName, originalIndex) => {
    const optimizedIndex = optimizationResult.optimizedOrder.findIndex(name => name === stopName);
    if (optimizedIndex !== -1) {
      originalToOptimized.set(originalIndex, optimizedIndex);
    }
  });

  // Create mapping: optimizedIndex -> originalIndex
  const optimizedToOriginal: Map<number, number> = new Map();
  optimizationResult.optimizedOrder.forEach((stopName, optimizedIndex) => {
    const originalIndex = optimizationResult.originalOrder.findIndex(name => name === stopName);
    if (originalIndex !== -1) {
      optimizedToOriginal.set(optimizedIndex, originalIndex);
    }
  });

  // Generate distinct colors for each stop
  const generateStopColor = (index: number, total: number): string => {
    const hue = (index * 360 / total) % 360;
    const saturation = 65;
    const lightness = 55;
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const stopColors = optimizationResult.originalOrder.map((_, idx) =>
    generateStopColor(idx, optimizationResult.originalOrder.length)
  );

  // Component for arrows that measures actual chip positions
  const ArrowConnector = () => {
    const [arrowPaths, setArrowPaths] = useState<Array<{ d: string; color: string; markerId: string }>>([]);
    const arrowsContainerRef = useRef<HTMLDivElement>(null);

    const calculatePaths = () => {
      if (!arrowsContainerRef.current) return;

      const containerRect = arrowsContainerRef.current.getBoundingClientRect();
      const paths: Array<{ d: string; color: string; markerId: string }> = [];

      optimizationResult!.originalOrder.forEach((_, originalIndex) => {
        const optimizedIndex = originalToOptimized.get(originalIndex);
        const moved = optimizedIndex !== undefined && optimizedIndex !== originalIndex;

        if (!moved || !originalChipRefs.current[originalIndex] || !optimizedChipRefs.current[optimizedIndex!]) return;

        const originalChip = originalChipRefs.current[originalIndex] as HTMLDivElement;
        const optimizedChip = optimizedChipRefs.current[optimizedIndex!] as HTMLDivElement;

        const originalRect = originalChip.getBoundingClientRect();
        const optimizedRect = optimizedChip.getBoundingClientRect();

        // Calculate relative positions within the arrows container
        const startX = originalRect.right - containerRect.left;
        const startY = originalRect.top + originalRect.height / 2 - containerRect.top;
        const endX = optimizedRect.left - containerRect.left;
        const endY = optimizedRect.top + optimizedRect.height / 2 - containerRect.top;

        const midX = (startX + endX) / 2;
        const controlY = (startY + endY) / 2;

        // Create smooth curved path
        const pathD = `M ${startX} ${startY} Q ${midX} ${startY} ${midX} ${controlY} T ${endX} ${endY}`;
        const markerId = `arrowhead-${originalIndex}`;

        paths.push({
          d: pathD,
          color: stopColors[originalIndex],
          markerId
        });
      });

      setArrowPaths(paths);
    };

    // Use useLayoutEffect to calculate after DOM is ready
    useLayoutEffect(() => {
      // Small delay to ensure all chips are rendered
      const timeoutId = setTimeout(() => {
        calculatePaths();
      }, 10);

      // Recalculate on window resize
      const handleResize = () => {
        calculatePaths();
      };
      window.addEventListener('resize', handleResize);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', handleResize);
      };
    }, [optimizationResult]);

    return (
      <Box
        ref={arrowsContainerRef}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 1,
          overflow: 'visible'
        }}
      >
        <svg
          width="100%"
          height="100%"
          style={{ overflow: 'visible' }}
        >
          <defs>
            {arrowPaths.map((path) => (
              <marker
                key={path.markerId}
                id={path.markerId}
                markerWidth="10"
                markerHeight="10"
                refX="9"
                refY="3"
                orient="auto"
              >
                <polygon
                  points="0 0, 10 3, 0 6"
                  fill={path.color}
                />
              </marker>
            ))}
          </defs>
          {arrowPaths.map((path, index) => (
            <path
              key={`path-${index}`}
              d={path.d}
              fill="none"
              stroke={path.color}
              strokeWidth="2.5"
              strokeLinecap="round"
              markerEnd={`url(#${path.markerId})`}
            />
          ))}
        </svg>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={false}
      disableAutoFocus={false}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          zIndex: 9999,
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9998,
          },
        },
      }}
    >
      <DialogContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Icon size={20} name="circle-check" style={{
              fontSize: 64,
              color: isAlreadyOptimal ? '#0288d1' : '#2e7d32',
              marginBottom: 16,
            }} />
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            {isAlreadyOptimal ? t('mapDialogs:routeOptimization.alreadyOptimalTitle') : t('mapDialogs:routeOptimization.successTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {isAlreadyOptimal
              ? t('mapDialogs:routeOptimization.alreadyOptimalBody')
              : t('mapDialogs:routeOptimization.reorderedBody')}
          </Typography>
        </Box>

        {/* Savings Highlight */}
        {isAlreadyOptimal ? (
          <Box
            sx={{
              bgcolor: 'info.light',
              color: 'info.contrastText',
              borderRadius: 2,
              p: 3,
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <Icon size={40} name="circle-check" />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {t('mapDialogs:routeOptimization.alreadyOptimalChipTitle')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {t('mapDialogs:routeOptimization.alreadyOptimalChipBody')}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              bgcolor: isImproved ? 'success.light' : 'warning.light',
              color: isImproved ? 'success.contrastText' : 'warning.contrastText',
              borderRadius: 2,
              p: 3,
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            {isImproved ? (
              <Icon size={40} d={ICON_FALLBACKS.TrendingDown} />
            ) : (
              <Icon size={40} d={ICON_FALLBACKS.TrendingUp} />
            )}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {isImproved
                  ? t('mapDialogs:routeOptimization.savedHeader', { distance: formatDistance(absDistanceSaved, distanceUnit), percent: absSavingsPercent })
                  : t('mapDialogs:routeOptimization.increasedHeader', { distance: formatDistance(absDistanceSaved, distanceUnit), percent: absSavingsPercent })}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {isImproved
                  ? t('mapDialogs:routeOptimization.savedBody', { from: formatDistance(optimizationResult.originalDistance, distanceUnit), to: formatDistance(optimizationResult.optimizedDistance, distanceUnit) })
                  : t('mapDialogs:routeOptimization.increasedBody', { from: formatDistance(optimizationResult.originalDistance, distanceUnit), to: formatDistance(optimizationResult.optimizedDistance, distanceUnit) })}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Route Comparison */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, position: 'relative' }}>
          {/* Original Order */}
          <Box sx={{ flex: 1, position: 'relative' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.secondary' }}>
              {t('mapDialogs:routeOptimization.originalOrder')}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', position: 'relative' }}>
              {optimizationResult.originalOrder.map((stopName, originalIndex) => {
                const optimizedIndex = originalToOptimized.get(originalIndex);
                const moved = optimizedIndex !== undefined && optimizedIndex !== originalIndex;
                return (
                  <Box
                    key={`original-${originalIndex}`}
                    id={`original-stop-${originalIndex}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      position: 'relative',
                      opacity: moved ? 1 : 0.7,
                      minHeight: 48
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, pr: 1 }}>{stopName}</Typography>
                    <Chip
                      ref={(el) => {
                        if (!originalChipRefs.current) originalChipRefs.current = [];
                        originalChipRefs.current[originalIndex] = el;
                      }}
                      label={originalIndex + 1}
                      size="small"
                      sx={{
                        minWidth: 36,
                        height: 24,
                        backgroundColor: stopColors[originalIndex],
                        color: 'white',
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                );
              })}
            </Paper>
          </Box>

          {/* Arrows Column */}
          <Box sx={{
            position: 'relative',
            minWidth: 120,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            overflow: 'visible'
          }}>
            <ArrowConnector />
            {optimizationResult.originalOrder.map((_, originalIndex) => (
              <Box
                key={`arrow-placeholder-${originalIndex}`}
                sx={{
                  height: 48,
                  width: '100%'
                }}
              />
            ))}
          </Box>

          {/* Optimized Order */}
          <Box sx={{ flex: 1, position: 'relative' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: isAlreadyOptimal ? 'info.main' : 'success.main' }}>
              {t('mapDialogs:routeOptimization.optimizedOrder')}
            </Typography>
            <Paper variant="outlined" sx={{
              p: 2,
              bgcolor: isAlreadyOptimal ? 'info.light' : 'success.light',
              borderColor: isAlreadyOptimal ? 'info.main' : 'success.main',
              borderWidth: 2,
              position: 'relative'
            }}>
              {optimizationResult.optimizedOrder.map((stopName, optimizedIndex) => {
                const originalIndex = optimizedToOriginal.get(optimizedIndex);
                const moved = originalIndex !== undefined && originalIndex !== optimizedIndex;
                const stopColor = originalIndex !== undefined ? stopColors[originalIndex] :
                  (isAlreadyOptimal ? 'var(--mui-palette-info-main)' : 'var(--mui-palette-success-main)');
                return (
                  <Box
                    key={`optimized-${optimizedIndex}`}
                    id={`optimized-stop-${optimizedIndex}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 1,
                      position: 'relative',
                      opacity: moved ? 1 : 0.7,
                      minHeight: 48
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, pr: 1 }}>{stopName}</Typography>
                    <Chip
                      ref={(el) => {
                        if (!optimizedChipRefs.current) optimizedChipRefs.current = [];
                        optimizedChipRefs.current[optimizedIndex] = el;
                      }}
                      label={optimizedIndex + 1}
                      size="small"
                      sx={{
                        minWidth: 36,
                        height: 24,
                        backgroundColor: stopColor,
                        color: 'white',
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                );
              })}
            </Paper>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="contained" color="primary" size="large" fullWidth>
          {t('common:close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

